/* eslint-disable @typescript-eslint/naming-convention */
import React, { useMemo } from 'react';
import { startCase } from 'lodash';
import { useHistory } from 'react-router-dom';
import {
  Badge,
  Button,
  Icon,
  IconButton,
  P,
  Stack,
  Tooltip,
  getDateTime,
} from '@fashionnovateam/nova-ui-toolkit';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';
import { PrintType, PurchaseOrder } from '../../Lib/Types/purchase-order';
import getPoStatus from '../../Lib/Utils/Helpers/make-button-text';
import PoStatus from '../../Lib/Constants/purchase-order-status';
import { User } from '../../Lib/Api/vendor';
import {
  canPrintUnitLabel,
  canPrintHangtag,
} from '../../Lib/Utils/Asn/printingApproved';
import { useCurrentUser } from '../../Lib/Contexts/CurrentUserContext';

type PoCardFooterProps = {
  purchaseOrder: PurchaseOrder;
  details?: boolean;
  handleAcceptModal?: () => void;
  handleRejectModal?: () => void;
  handlePrintUnitLabelModal?: () => void;
  handlePrintHangTagModal?: () => void;
  perms: {
    canAccept: boolean;
    canPrint: boolean;
  };
  user: User;
  onProp65DrawerOpen?: () => void;
};

// is true if all items are printed
const getItemsStatus = (po: PurchaseOrder, printType: PrintType) =>
  po.purchase_order_lines
    .filter((item) => !!item.catalog_item)
    .reduce((status, item) => !!item[printType] && status, true);

export function PoCardFooter({
  purchaseOrder,
  details,
  handleAcceptModal,
  handleRejectModal,
  handlePrintUnitLabelModal,
  handlePrintHangTagModal,
  perms,
  user,
  onProp65DrawerOpen,
}: PoCardFooterProps) {
  const { flags } = React.useContext(FeatureFlagsContext);
  const history = useHistory();

  const {
    is_accepted,
    date_of_acceptance,
    vendor_rejection_reason,
    revision_accepted_at,
  } = purchaseOrder;

  const isProp65Completed = useMemo(
    () =>
      purchaseOrder.purchase_order_lines.every((line) => line.prop65 !== null),
    [purchaseOrder],
  );

  const kidsDivision = React.useMemo(
    () => purchaseOrder.division === "KID'S",
    [purchaseOrder],
  );

  const printUnitLabelStatus = getItemsStatus(
    purchaseOrder,
    'dual_sticker_status',
  );
  const printHangtagStatus = getItemsStatus(
    purchaseOrder,
    'hangtag_sticker_status',
  );

  const acceptButtonText = getPoStatus({
    revised_at: purchaseOrder.revised_at,
    vendor_rejection_reason: purchaseOrder.vendor_rejection_reason,
    revision_accepted_at: purchaseOrder.revision_accepted_at,
  });

  const acceptDisabled =
    acceptButtonText === PoStatus.ACCEPTED ||
    acceptButtonText === PoStatus.PENDING;

  const { currentUser } = useCurrentUser();

  const canPrintBase =
    perms.canPrint &&
    acceptButtonText !== PoStatus.ACCEPT_REVISION &&
    (!flags.prop65?.enabled || isProp65Completed);

  const showPrintUnitLabel =
    canPrintBase && canPrintUnitLabel(user, currentUser);
  const showPrintHangtag = canPrintBase && canPrintHangtag(user, currentUser);
  const showAnyPrintButton = showPrintUnitLabel || showPrintHangtag;

  return (
    <Stack direction="col" gap="md">
      <Stack
        direction="row"
        align="center"
        className={
          details ? 'gap-3' : 'border-t border-neutral-200 py-[6px] px-3 gap-3'
        }
      >
        <Stack gap="xs" direction="row" className="my-[6px]">
          {purchaseOrder.purchase_type && (
            <Badge contrast="low">{purchaseOrder.purchase_type}</Badge>
          )}
          {purchaseOrder.order_type && (
            <Badge contrast="low">{purchaseOrder.order_type}</Badge>
          )}
          {purchaseOrder.country_of_origin && (
            <Badge contrast="low">{purchaseOrder.country_of_origin}</Badge>
          )}
          {revision_accepted_at && (
            <Tooltip
              content={getDateTime(revision_accepted_at, false, 'UTC')}
              position="top"
              arrow
            >
              <Badge contrast="low">
                {revision_accepted_at ? 'Revision Accepted' : 'Accepted'}
              </Badge>
            </Tooltip>
          )}
        </Stack>
        {!details && (
          <Stack direction="row" gap="xs" width="fit">
            {showAnyPrintButton && (
              <P className="text-xs text-[#1D24338C] pr-2 self-center">Print</P>
            )}
            {showPrintUnitLabel && (
              <Tooltip content="Print Unit Label" position="top" arrow>
                <Button
                  size="sm"
                  color="secondary"
                  className="*:size-4"
                  onClick={handlePrintUnitLabelModal}
                >
                  <Icon name="bag/paper-bag" />
                  {printUnitLabelStatus ? (
                    <Icon
                      name="check/check-tick-circle"
                      variant="solid"
                      className="text-success-700 size-4"
                    />
                  ) : (
                    <Icon name="circle-dotted" />
                  )}
                </Button>
              </Tooltip>
            )}
            {showPrintHangtag && (
              <Tooltip content="Print Hangtags" position="top" arrow>
                <Button
                  size="sm"
                  color="secondary"
                  className="*:size-4"
                  onClick={handlePrintHangTagModal}
                >
                  <Icon name="tag" />
                  {printHangtagStatus ? (
                    <Icon
                      name="check/check-tick-circle"
                      variant="solid"
                      className="text-success-700"
                    />
                  ) : (
                    <Icon name="circle-dotted" />
                  )}
                </Button>
              </Tooltip>
            )}
            {!purchaseOrder.is_accepted && perms.canAccept && (
              <>
                {!acceptDisabled ? (
                  <P className="text-xs text-[#1D24338C] pr-2 self-center whitespace-nowrap">
                    {startCase(acceptButtonText)}
                  </P>
                ) : null}
                {acceptButtonText === PoStatus.ACCEPTED ? null : (
                  <Tooltip
                    content={startCase(acceptButtonText)}
                    position="top"
                    arrow
                  >
                    <IconButton
                      label="approve"
                      disabled={acceptDisabled}
                      onClick={
                        acceptButtonText === PoStatus.ACCEPT_REVISION
                          ? () =>
                              history.push(
                                `${history.location.pathname}/${purchaseOrder.po_number}`,
                              )
                          : handleAcceptModal
                      }
                      icon={<Icon.CheckTickCircle />}
                    />
                  </Tooltip>
                )}
              </>
            )}
            {!is_accepted && !date_of_acceptance && false && (
              <IconButton
                size="sm"
                color="secondary"
                label="reject"
                disabled={
                  !!vendor_rejection_reason ||
                  !!date_of_acceptance ||
                  !!is_accepted
                }
                onClick={handleRejectModal}
                icon={<Icon.MultipleCrossCancelDefault />}
              />
            )}
            {flags.prop65 &&
              flags.prop65.enabled &&
              !isProp65Completed &&
              !kidsDivision && (
                <Button
                  onClick={onProp65DrawerOpen}
                  color="secondary"
                  size="md"
                  className="text-xs text-[#1D2433CC] self-center whitespace-nowrap"
                >
                  Set CA Prop 65
                </Button>
              )}
          </Stack>
        )}
      </Stack>
      {details && (
        <div className="flex gap-3 items-center pl-4 pr-[15px] py-3 rounded-tl-3xl rounded-tr-3xl rounded-br-3xl bg-neutral-100 w-fit">
          <Icon.ChatDefault className="text-base-disabled" />
          <P color="disabled" size="xsm" className="whitespace-pre-line">
            {purchaseOrder.vendor_message || 'No messages.'}
          </P>
        </div>
      )}
    </Stack>
  );
}
